import { useState, useEffect, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { posthog } from '@/lib/posthog';
import { logger } from '@/lib/logger';

export interface AuthUser extends User {
  role?: string;
  company_id?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
}

const IS_DEV = import.meta.env.DEV;

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const queryClient = useQueryClient();
  
  // Guard flags to avoid race conditions during logout
  const isLoggingOutRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        currentUserIdRef.current = session?.user?.id ?? null;

        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }

        if (session?.user) {
          // Use queueMicrotask to defer without adding setTimeout delay
          queueMicrotask(async () => {
            if (isLoggingOutRef.current) {
              return;
            }
            if (!currentUserIdRef.current || currentUserIdRef.current !== session.user.id) {
              return;
            }
            await loadUserData(session.user);
          });
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    // Set up global logout listener for synchronization across all useAuth instances
    const handleGlobalLogout = () => {
      setUser(null);
      setSession(null);
      currentUserIdRef.current = null;
      setLoading(false);
    };
    
    window.addEventListener('tekton:auth:logout', handleGlobalLogout);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('tekton:auth:logout', handleGlobalLogout);
    };
  }, []);

  const loadUserData = useCallback(async (authUser: User) => {
    try {
      if (isLoggingOutRef.current) {
        return;
      }
      if (!authUser || (currentUserIdRef.current && currentUserIdRef.current !== authUser.id)) {
        return;
      }

      // Check React Query cache first for instant data
      const cachedUserData = queryClient.getQueryData<{
        role: string | null;
        company_id: string | null;
        company_name: string | null;
        first_name: string | null;
        last_name: string | null;
      }>(['user-cache', authUser.id]);

      if (cachedUserData) {
        setUser({
          ...authUser,
          role: cachedUserData.role || undefined,
          company_id: cachedUserData.company_id || undefined,
          first_name: cachedUserData.first_name || undefined,
          last_name: cachedUserData.last_name || undefined,
          company_name: cachedUserData.company_name || undefined,
        });
        setLoading(false);
        return;
      }

      // OPTIMIZED: Single RPC call instead of 2 parallel queries (~200ms faster)
      const { data: profileData, error: profileError } = await supabase
        .rpc('get_user_profile_with_role', { p_user_id: authUser.id })
        .single();
      
      if (profileError) {
        logger.error("useAuth", "Profile+Role fetch error:", profileError);
      }

      const userWithData = {
        ...authUser,
        role: profileData?.role,
        company_id: profileData?.company_id,
        first_name: profileData?.first_name,
        last_name: profileData?.last_name,
        company_name: profileData?.company_name
      };

      // Update React Query cache for future use
      queryClient.setQueryData(['user-cache', authUser.id], {
        id: authUser.id,
        email: authUser.email || '',
        role: profileData?.role || null,
        company_id: profileData?.company_id || null,
        company_name: profileData?.company_name || null,
        first_name: profileData?.first_name || null,
        last_name: profileData?.last_name || null,
      });

      // Identify user in PostHog for analytics
      posthog.identify(userWithData.id, {
        email: userWithData.email,
        role: userWithData.role,
        company_id: userWithData.company_id,
        company_name: userWithData.company_name,
      });


      setUser(userWithData);
    } catch (error) {
      logger.error("useAuth", "Error fetching user data:", error);
      setUser({
        ...authUser,
        role: undefined,
        company_id: undefined,
        first_name: undefined,
        last_name: undefined,
        company_name: undefined
      });
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  const refreshUserData = async () => {
    if (session?.user) {
      await loadUserData(session.user);
    }
  };

  const signUp = async (email: string, password: string, userData: {
    firstName: string;
    lastName: string;
    companyName: string;
  }) => {
    const redirectUrl = `${window.location.origin}/confirm`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          company_name: userData.companyName,
          role: 'company_director'
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        // Si rememberMe = false, la session expire à la fermeture du navigateur
        // Si rememberMe = true, la session persiste (défaut Supabase: 1 semaine)
        ...(rememberMe ? {} : { 
          data: { session_type: 'temporary' }
        })
      }
    });
    
    if (error) {
      logger.error("useAuth", "Sign in error:", error);
    }
    
    return { error };
  };

  const broadcastLogout = () => {
    // CRITICAL: Cancel all queries and clear cache FIRST to prevent race conditions
    // This stops any in-flight requests from firing during logout transition
    queryClient.cancelQueries();
    queryClient.clear();
    
    // Clean up localStorage
    try {
      localStorage.removeItem('selected_site_id');
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        const lower = key.toLowerCase();
        if (lower.startsWith('sb-') || lower.includes('supabase.auth.token')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => {
        try { localStorage.removeItem(k); } catch {}
      });
      if (IS_DEV && keysToRemove.length) {
      }
    } catch (e) {
    }
    
    // Remove all Supabase channels to avoid interference
    try {
      supabase.removeAllChannels();
    } catch (e) {
    }
    
    // Reset PostHog identity on logout
    posthog.reset();

    // Dispatch global logout event to synchronize all useAuth instances
    window.dispatchEvent(new CustomEvent('tekton:auth:logout'));
  };

  const signOut = async () => {
    isLoggingOutRef.current = true;
    
    try {
      // Check if we have a valid session before attempting to sign out
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // No active session, just clear local state
        setUser(null);
        setSession(null);
        currentUserIdRef.current = null;
        broadcastLogout();
        return { error: null };
      }

      const { error } = await supabase.auth.signOut();
      
      // Always clear local state and broadcast logout, regardless of error
      setUser(null);
      setSession(null);
      currentUserIdRef.current = null;
      
      // If the error is "session_not_found", consider it a successful logout
      // since the user is effectively already logged out
      if (error?.message?.includes('session_not_found') || error?.message?.includes('Session not found')) {
        broadcastLogout();
        return { error: null };
      }
      
      if (error) {
        logger.error("useAuth", "Sign out error:", error);
        broadcastLogout();
        return { error };
      }
      
      broadcastLogout();
      return { error: null };
    } catch (error: any) {
      logger.error("useAuth", "Unexpected error during sign out:", error);
      // If there's an unexpected error, still clear local state
      setUser(null);
      setSession(null);
      currentUserIdRef.current = null;
      broadcastLogout();
      return { error: null };
    } finally {
      isLoggingOutRef.current = false;
    }
  };

  const clearPasswordRecovery = useCallback(() => {
    setIsPasswordRecovery(false);
  }, []);

  return {
    user,
    session,
    loading,
    isPasswordRecovery,
    clearPasswordRecovery,
    signUp,
    signIn,
    signOut,
    refreshUserData
  };
};